import React, { useState, useRef, useEffect, useMemo } from 'react';
import { uniqueId } from 'lodash';
import styles from './MultiSelectDropDown.module.scss';
import classNames from 'classnames';
import { Icon } from 'Components';
import InfinityScrollWrapper from 'Components/InfinityScroll/InfinityScrollWrapper';
import { useTranslation } from 'react-i18next';

const PAGED_LIST_HEIGHT = 240;

interface Option {
	id: number;
	label: string;
}

interface MultiSelectDropdownProps {
	options: Option[];
	placeholder?: string;
	onChange?: (selectedIds: number[], allSelected: boolean) => void;
	defaultSelectedIds?: number[];
	selectAllMode?: boolean;
	defaultAllSelected?: boolean;
	className?: string;
	allLabel?: string;
	showSelectedLabels?: boolean;
	searchable?: boolean;
	searchPlaceholder?: string;
	loading?: boolean;
	hasMore?: boolean;
	onLoadMore?: () => void;
	onSearch?: (query: string) => void;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
	options,
	placeholder,
	onChange,
	defaultSelectedIds = [],
	selectAllMode = false,
	defaultAllSelected = false,
	className,
	allLabel,
	showSelectedLabels = false,
	searchable = false,
	searchPlaceholder,
	loading = false,
	hasMore = false,
	onLoadMore,
	onSearch
}) => {
	const { t } = useTranslation();

	const translatedPlaceholder =
		placeholder ?? t('learning.multiSelectDropdown.selectOption');
	const translatedAllLabel = allLabel ?? t('learning.multiSelectDropdown.all');
	const translatedSearchPlaceholder =
		searchPlaceholder ?? t('learning.multiSelectDropdown.search');

	const [isOpen, setIsOpen] = useState(false);
	const [selectedIds, setSelectedIds] = useState<number[]>(
		defaultSelectedIds
	);
	const [isAllMode, setIsAllMode] = useState(
		selectAllMode && defaultAllSelected
	);
	const [query, setQuery] = useState('');
	const dropdownRef = useRef<HTMLDivElement>(null);
	const scrollableId = useRef(uniqueId('multi-select-options-')).current;

	const trimmedQuery = query.trim().toLowerCase();
	const isServerSearch = !!onSearch;

	const visibleOptions = useMemo(
		() =>
			trimmedQuery && !isServerSearch
				? options.filter((opt) =>
						opt.label.toLowerCase().includes(trimmedQuery)
				)
				: options,
		[options, trimmedQuery, isServerSearch]
	);

	const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

	useEffect(() => {
		if (!isOpen) setQuery('');
	}, [isOpen]);

	useEffect(() => {
		onSearch?.(query);
		const list = document.getElementById(scrollableId);
		if (list) list.scrollTop = 0;
	}, [query]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () =>
			document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const toggleDropdown = () => {
		setIsOpen(!isOpen);
	};

	const handleSelectAll = (checked: boolean) => {
		if (selectAllMode) {
			setIsAllMode(checked);
			setSelectedIds([]);
			onChange?.([], checked);
			return;
		}

		const newSelectedIds = checked ? options.map((opt) => opt.id) : [];
		setSelectedIds(newSelectedIds);
		onChange?.(newSelectedIds, false);
	};

	const handleOptionToggle = (id: number, checked: boolean) => {
		if (isAllMode) return;

		const newSelectedIds = checked
			? [...selectedIds, id]
			: selectedIds.filter((selectedId) => selectedId !== id);

		setSelectedIds(newSelectedIds);
		onChange?.(newSelectedIds, false);
	};

	const isAllSelected = selectAllMode
		? isAllMode
		: !hasMore &&
			!(isServerSearch && trimmedQuery) &&
			selectedIds.length === options.length &&
			options.length > 0;

	const isSomeSelected = selectedIds.length > 0 && !isAllSelected;

	const getDisplayText = () => {
		if (isAllMode) return translatedAllLabel;
		if (selectedIds.length === 0) return translatedPlaceholder;

		if (showSelectedLabels) {
			if (isAllSelected && options.length > 1) {
				return translatedAllLabel;
			}

			return options
				.filter((opt) => selectedIds.includes(opt.id))
				.map((opt) => opt.label)
				.join(', ');
		}

		const value = isAllSelected
			? translatedAllLabel
			: selectedIds.length === 1
			? options.find((opt) => opt.id === selectedIds[0])?.label ??
			  t('learning.multiSelectDropdown.selectedCount', {
					count: 1
			  })
			: t('learning.multiSelectDropdown.selectedCount', {
					count: selectedIds.length
			  });

		return value || translatedPlaceholder;
	};

	const withInfiniteScroll = (list: React.ReactNode) =>
		onLoadMore ? (
			<InfinityScrollWrapper
				dataLength={options.length}
				hasMore={hasMore && (isServerSearch || !trimmedQuery)}
				onLoadMore={onLoadMore}
				height={PAGED_LIST_HEIGHT}
				scrollableId={scrollableId}
				endMessageText=""
			>
				{list}
			</InfinityScrollWrapper>
		) : (
			list
		);

	return (
		<div
			className={classNames(styles.multiSelect, className)}
			ref={dropdownRef}
		>
			<div
				className={classNames(styles.trigger, {
					[styles['trigger--open']]: isOpen
				})}
				onClick={toggleDropdown}
			>
				<span className={styles.triggerText} title={getDisplayText()}>
					{getDisplayText()}
				</span>
				<Icon
					type="chevronDown"
					style={{
						transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
					}}
				/>
			</div>

			{isOpen && (
				<div className={styles.dropdown}>
					{searchable && (
						<div className={styles.searchBox}>
							<input
								type="text"
								className={styles.searchInput}
								placeholder={translatedSearchPlaceholder}
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								autoFocus
							/>
						</div>
					)}

					{withInfiniteScroll(
						<div className={styles.optionList}>
							{loading && !options.length && (
								<div className={styles.noResults}>
									{t('learning.multiSelectDropdown.loading')}
								</div>
							)}

							{!trimmedQuery && (
								<label className={styles.option}>
									<div className={styles.checkboxWrapper}>
										<input
											type="checkbox"
											checked={isAllSelected}
											ref={(input) => {
												if (input) {
													input.indeterminate =
														isSomeSelected;
												}
											}}
											onChange={(e) =>
												handleSelectAll(
													e.target.checked
												)
											}
											className={styles.checkbox}
										/>
										<span
											className={classNames(
												styles.checkmark,
												{
													[styles[
														'checkmark--indeterminate'
													]]: isSomeSelected
												}
											)}
										>
											{isAllSelected && (
												<svg
													width="12"
													height="10"
													viewBox="0 0 12 10"
													fill="none"
												>
													<path
														d="M1 5L4.5 8.5L11 1"
														stroke="white"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
											)}
											{isSomeSelected &&
												!isAllSelected && (
													<svg
														width="12"
														height="2"
														viewBox="0 0 12 2"
														fill="none"
													>
														<path
															d="M1 1H11"
															stroke="white"
															strokeWidth="2"
															strokeLinecap="round"
														/>
													</svg>
												)}
										</span>
									</div>
									<span className={styles.optionLabel}>
										{translatedAllLabel}
									</span>
								</label>
							)}

							{!loading && !visibleOptions.length && (
								<div className={styles.noResults}>
									{t('learning.multiSelectDropdown.noResults')}
								</div>
							)}

							{visibleOptions.map((option) => (
								<label
									key={option.id}
									className={classNames(styles.option, {
										[styles['option--disabled']]: isAllMode
									})}
								>
									<div className={styles.checkboxWrapper}>
										<input
											type="checkbox"
											checked={
												isAllMode ||
												selectedIdSet.has(option.id)
											}
											disabled={isAllMode}
											onChange={(e) =>
												handleOptionToggle(
													option.id,
													e.target.checked
												)
											}
											className={styles.checkbox}
										/>
										<span className={styles.checkmark}>
											{(isAllMode ||
												selectedIdSet.has(
													option.id
												)) && (
												<svg
													width="12"
													height="10"
													viewBox="0 0 12 10"
													fill="none"
												>
													<path
														d="M1 5L4.5 8.5L11 1"
														stroke="white"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
											)}
										</span>
									</div>
									<span className={styles.optionLabel}>
										{option.label}
									</span>
								</label>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default MultiSelectDropdown;
